import { useEffect, useId, useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Button } from '@/components/Button';

/**
 * ファイル選択フィールド (v0.4 追加要件⑰ で新設)。
 *
 * ⚠️ ネイティブの `<input type="file">` はブラウザ既定のボタンが 44px のタップ領域を満たさず、
 *  CLAUDE.md §9 の完了条件を機械判定 (`design-check.js` の `checkTapTargetSize`) で満たせない。
 *  ⚠️ **手本になる既存実装が無い** — 設計書が参照した `AiOcrPage.tsx` も生 input のままだった
 *  (design-expert H-4 の実測で判明) ため、ここで新規に作る。
 *
 * input 自体は `sr-only` で残す (キーボード操作・スクリーンリーダー・フォーム送信のため)。
 * ⚠️ `hidden` や `display:none` にしないこと — フォーカスを受け取れなくなる。
 */
export function FilePickerField({
  label, accept, required = false, disabled = false, hint, onChange, capture, preview = false, onCapture,
}: {
  label: string;
  accept: string;
  required?: boolean;
  disabled?: boolean;
  hint?: React.ReactNode;
  onChange: (file: File | null) => void;
  /** v0.5① 日報機能 §14-5-2: モバイルでタップした瞬間にカメラを直接起動する (ファイル選択
   *  ダイアログを経由しない)。日報の添付では既定で `'environment'` (背面カメラ) を渡す。 */
  capture?: 'environment';
  /** 選択後にサムネイルを表示する (`AiOcrReviewPage.tsx` の `PageImage` と同じ
   *  createObjectURL/revokeObjectURL パターン)。 */
  preview?: boolean;
  /** `onChange` とは別に、ファイルが選ばれた瞬間のトリガーとして使う (P-4 のアップロード開始用)。 */
  onCapture?: (file: File) => void;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  return (
    <div>
      <label htmlFor={id} className="text-caption text-text-muted">
        {label}{required && <span className="text-alert-text ml-1" aria-hidden>*</span>}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        required={required}
        disabled={disabled}
        capture={capture}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setName(f?.name ?? null);
          if (preview) {
            setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return f ? URL.createObjectURL(f) : null; });
          }
          onChange(f);
          if (f) onCapture?.(f);
        }}
      />
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        {/* ⚠️ 独自のクラスでボタンを組まない。既存トークンに無いクラス名は Tailwind が
            生成せず「色が付かない」事故になる (design-expert C-1 の実例)。既存 Button を使う。 */}
        <Button type="button" variant="secondary" className="min-h-[44px]" disabled={disabled}
          onClick={() => inputRef.current?.click()}>
          <Paperclip className="w-4 h-4 mr-1.5" aria-hidden />
          {capture ? '写真を撮る' : 'ファイルを選択'}
        </Button>
        <span className="text-body text-text-sub break-all min-w-0">
          {name ?? '選択されていません'}
        </span>
      </div>
      {preview && previewUrl && (
        <img src={previewUrl} alt="選択したファイルのプレビュー" className="mt-2 max-w-full max-h-64 rounded border border-surface-border" />
      )}
      {hint && <p className="mt-1 text-body text-text-sub">{hint}</p>}
    </div>
  );
}
